/**
 * Utility functions for comparison charts
 *
 * This module provides helper functions for rendering multi-asset comparison charts,
 * including ticker name sanitization and color assignment.
 */

/**
 * Sanitizes ticker symbol to match backend column naming
 * Backend converts special characters to underscores for SQL compatibility
 *
 * Examples:
 * - BTC-USD → BTC_USD
 * - GC=F → GC_F
 * - ES.F → ES_F
 *
 * @param ticker - The ticker symbol to sanitize
 * @returns Sanitized ticker name matching backend column name
 */
export function sanitizeTickerName(ticker: string): string {
  return ticker.replace(/[-=.]/g, '_');
}

/**
 * Color palette for distinguishing multiple assets on comparison charts
 * Uses visually distinct colors optimized for accessibility
 *
 * Supports up to 12 assets before colors repeat
 */
export const COMPARISON_COLORS = [
  '#2962FF', // Blue
  '#FF6D00', // Orange
  '#4CAF50', // Green
  '#E91E63', // Pink
  '#9C27B0', // Purple
  '#00BCD4', // Cyan
  '#FF5722', // Deep Orange
  '#8BC34A', // Light Green
  '#FFC107', // Amber
  '#795548', // Brown
  '#607D8B', // Blue Grey
  '#F44336', // Red
] as const;

/**
 * Gets color for an asset by index
 * Colors wrap around if more than 12 assets
 *
 * @param index - The index of the asset (0-based)
 * @returns Hex color code
 */
export function getColorForIndex(index: number): string {
  return COMPARISON_COLORS[index % COMPARISON_COLORS.length];
}

/**
 * Determines line width based on number of assets
 * Reduces width for charts with many assets to prevent crowding
 *
 * @param assetCount - Number of assets being compared
 * @returns Line width in pixels
 */
export function getLineWidth(assetCount: number): number {
  if (assetCount > 5) return 1;
  return 2;
}

/**
 * Data point interface for comparison charts
 * Each point has a timestamp and values for multiple assets
 */
export interface ComparisonDataPoint {
  time: string;
  [assetColumn: string]: number | string; // Asset columns use sanitized ticker names
}

/**
 * Series interface for TradingView rendering
 */
export interface ComparisonSeries {
  name: string;
  columnName: string;
  data: Array<{ time: number; value: number }>;
  color: string;
  lineWidth: number;
}

/**
 * Transforms comparison datapack into series for TradingView
 *
 * @param data - Raw comparison datapack from API
 * @param tickers - Array of ticker display names (for legend)
 * @param columnNames - Array of sanitized column names (for data access)
 * @returns Array of series ready for chart rendering
 */
export function transformComparisonData(
  data: ComparisonDataPoint[],
  tickers: string[],
  columnNames: string[]
): ComparisonSeries[] {
  const lineWidth = getLineWidth(tickers.length);

  return tickers.map((ticker, index) => {
    // Use the provided column name from backend, not sanitized ticker
    const columnName = columnNames[index];

    return {
      name: ticker, // Display name for legend
      columnName, // Backend column name for data access
      data: data
        .map(point => ({
          time: new Date(point.time).getTime() / 1000, // Convert to Unix seconds
          value: typeof point[columnName] === 'number' ? point[columnName] : NaN,
        }))
        .filter(point => !isNaN(point.value)), // Filter out null/NaN values
      color: getColorForIndex(index),
      lineWidth,
    };
  });
}
